import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RiCloseLine } from 'react-icons/ri';
import GlassButton from '../ui/v2/GlassButton';

interface ProfileSidePanelProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    onSave: () => void;
    isSaving?: boolean;
    saveLabel?: string;
}

export default function ProfileSidePanel({
    isOpen,
    onClose,
    title,
    children,
    onSave,
    isSaving = false,
    saveLabel = 'Save Changes'
}: ProfileSidePanelProps) {
    // Prevent body scroll when panel is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[9999] bg-[rgba(12,14,20,0.7)] backdrop-blur-[4px]"
                    />

                    {/* Panel */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{
                            type: 'tween',
                            duration: 0.22,
                            ease: [0.4, 0, 0.2, 1]
                        }}
                        className="fixed top-0 right-0 z-[10000] h-full w-full sm:w-[600px] md:w-[700px] bg-[#0c0e14]/95 backdrop-blur-[20px] border-l border-white/5 shadow-2xl flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                            <h2 className="text-xl font-semibold text-white">{title}</h2>
                            <button
                                onClick={onClose}
                                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                            >
                                <RiCloseLine size={24} />
                            </button>
                        </div>

                        {/* Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar">
                            <div className="space-y-6">
                                {children}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-5 border-t border-white/5 bg-[#0c0e14]/50 backdrop-blur-sm flex items-center justify-end gap-3 mt-auto">
                            <button
                                onClick={onClose}
                                disabled={isSaving}
                                className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <GlassButton
                                onClick={onSave}
                                disabled={isSaving}
                                className="!px-6 !py-2 !h-auto !text-sm whitespace-nowrap"
                            >
                                {isSaving ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Saving...
                                    </span>
                                ) : (
                                    saveLabel
                                )}
                            </GlassButton>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}
